import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { property, query, queryAll, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { desanitizeObject } from '../../../utils/sanitization.utils';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { GzCheckboxWebComponent } from '../../gzCheckbox';
import { GzInputSearchWebComponent } from '../../gzInput';
import { WEB_COMPONENT_GZ_INPUT_SEARCH } from '../../../webcomponents.constants';
import { EnumListEvents, EnumListItem, GroupType, ItemType, SelectedOption } from '../types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { groupBy } from 'lodash';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';

export class GzEnumListWebComponent extends LitElement {
  /**
   * Get only the filterable checkboxes from the list; we don't want to get the value or label from the 'All' checkbox.
   */
  public get checkboxesList() {
    if (!this.shadowRoot) {
      return [];
    }

    return this.filteredCheckboxes(this.shadowRoot.querySelectorAll('.is-filterable'));
  }

  public get enabledCheckboxes(): GzCheckboxWebComponent[] {
    return this.checkboxesList.filter((checkbox: GzCheckboxWebComponent) => this.isOptionEnabled(checkbox));
  }

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  private static optionsListMaxLength = 6;
  private static groupKey = 'parent';

  @property({ type: Array, attribute: true, reflect: true })
  filterOptions: EnumListItem[] = [];

  /**
   * This property will store the selected values from the dropdown. It will be updated when the user confirms the
   * selection by clicking the Apply button.
   */
  @state()
  private selectedOptions: SelectedOption[] = [];

  /**
   * Stores the search results grouped by the 'parent' key
   */
  @state()
  private groupedSearchResult: Record<string, EnumListItem[]>;

  @property({ type: String, attribute: true, reflect: true })
  searchPlaceholderString = '';

  @property({ type: String, attribute: true, reflect: true })
  noSearchResultsString = '';

  /**
   * Use this property when you want different text from noSearchResultsString
   * for no results in default group
   */
  @property({ type: String, attribute: true, reflect: true })
  noResultsInDefaultGroup;

  /**
   * This property will display a title above the filterable list items
   */
  @property({ type: String, attribute: false })
  listTitle = '';

  /**
   * If this property is set to true, an 'ALL' checkbox will be displayed above the list items.
   * Checking/unchecking this checkbox will also toggle the checked state on all the checkboxes in the list
   */
  @property({ type: Boolean, reflect: true })
  canSelectAll = false;

  @property({ type: String, attribute: true, reflect: true })
  allItemsLabel = '';

  @property({ type: String, attribute: true, reflect: true })
  groupType: GroupType = GroupType.DEFAULT;

  @property({ type: Number, attribute: true, reflect: true })
  searchMaxLength;

  @query('#emptyTemplate')
  private readonly emptyTemplate!: HTMLElement;

  @queryAll('.option-selectable')
  protected optionsList!: NodeList;

  @query(WEB_COMPONENT_GZ_INPUT_SEARCH)
  public searchInput!: GzInputSearchWebComponent;

  @query('#allCheckboxesSelect')
  protected readonly allCheckboxesSelect!: HTMLElement;

  @queryAll('.list-group')
  private listGroup!: NodeList;

  @state()
  private isSelectAllChecked = false;

  /**
   * This internal reactive property helps re-render the list items: when TRUE, the list items need to be rendered as ungrouped.
   * When FALSE, the list items are rendered as grouped.
   */
  @state()
  protected listIsFiltered = false;

  /**
   * This will store the filtered values.
   */

  private searchResult: EnumListItem[];

  /**
   * Only return children nodes that are GzCheckboxWebComponent
   */
  filteredCheckboxes(nodes): GzCheckboxWebComponent[] {
    return Array.from(nodes).filter(child => child instanceof GzCheckboxWebComponent) as GzCheckboxWebComponent[];
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('filterOptions')) {
      this.updateEmptyTemplateDisplay(
        isEmpty(this.filterOptions) || (isEmpty(this.searchResult) && this.listIsFiltered)
      );
      this.setSelectedOptions(this.getSelectedValuesFromOptionsList());
      this.matchCheckboxesToSelectedOptions();

      if (this.canSelectAll) {
        this.updateSelectAllCheckboxesState();
      }
    }
  }

  render() {
    const hasSearchInput = this.hasSearchInput();
    return html`
      <div class="list ${hasSearchInput ? 'has-search' : ''}">
        <div class="list--search">${this.renderSearchInput()}</div>

        ${this.renderListTitle()} ${this.renderListOptions()}
      </div>
    `;
  }

  /**
   * https://lit-element.polymer-project.org/api/classes/_lit_element_.templateresult.html
   */
  private renderListTitle(): TemplateResult | undefined {
    if (isEmpty(this.listTitle)) {
      return;
    }

    return html` <div class="list--title text-semibold">${this.listTitle}</div> `;
  }

  protected renderSearchInput() {
    return html`<gz-input-search
      placeholder="${this.searchPlaceholderString}"
      inputWidth="medium"
      .disabled="${isEmpty(this.filterOptions)}"
      .maxLength="${ifDefined(this.searchMaxLength)}"
      @change="${this.handleOptionsSearch}"
    ></gz-input-search>`;
  }

  protected renderListOptions() {
    return html`<div class="list--options" part="listOptions">
      ${this.renderNoResultsTemplate()} ${this.renderSelectAllItemsCheckbox()} ${this.renderFilterOptionsList()}
    </div>`;
  }

  protected renderNoResultsTemplate() {
    return html`<div id="emptyTemplate" class="list--no-results">${this.noSearchResultsString}</div>`;
  }

  protected renderSelectAllItemsCheckbox(): TemplateResult | undefined {
    if (!this.canSelectAll || isEmpty(this.filterOptions)) {
      return;
    }

    return html`
      <div id="allCheckboxesSelect" class="option option-all" part="optionAll">
        <gz-checkbox
          class="full-checkbox"
          value="all"
          label="${this.allItemsLabel}"
          ?checked="${this.isSelectAllChecked}"
          ?indeterminate="${this.isSelectAllIndeterminate()}"
          @change="${this.toggleAllCheckboxes}"
        ></gz-checkbox>
      </div>
    `;
  }

  protected renderFilterOptionsList() {
    if (isEmpty(this.filterOptions)) {
      return;
    }
    const groupedFilterOptions = this.listIsFiltered
      ? this.groupedSearchResult
      : groupBy(this.filterOptions, GzEnumListWebComponent.groupKey);
    if (this.groupType === GroupType.DEFAULT && this.listIsFiltered) {
      return html`${this.filterOptions.map(option => this.renderFilterOption(option))}`;
    }
    return Object.entries(groupedFilterOptions).map(([groupTitle, options], index) =>
      this.renderFilterOptionsGroup(groupTitle, options, index)
    );
  }

  protected updateFilteredCheckboxesBySearchResult(searchResult: EnumListItem[]) {
    if (this.groupType === GroupType.ACCORDION) {
      return;
    }
    this.filterOptions.forEach((option: EnumListItem, index: number) => {
      const item = this.optionsList[index] as HTMLElement;

      if (!item) {
        return;
      }

      if (searchResult.includes(option)) {
        elementClassListRemove(item, ElementClass.HIDDEN);
      } else {
        elementClassListAdd(item, ElementClass.HIDDEN);
      }
    });
  }

  private renderFilterOptionsGroup(groupTitle: string, options: EnumListItem[], groupIndex: number): TemplateResult {
    return this.groupType === GroupType.ACCORDION && !this.listIsFiltered
      ? this.renderItemsInAccordionGroup(groupTitle, options)
      : this.renderItemsInDefaultGroup(groupTitle, options, groupIndex);
  }

  private renderItemsInAccordionGroup(groupTitle: string, options: EnumListItem[]): TemplateResult {
    return html`
      <gz-accordion .accordionTitle="${groupTitle.toUpperCase()}" class="accordion-group" iscompact>
        ${options.map(option => this.renderFilterOption(option))}
      </gz-accordion>
    `;
  }

  private renderItemsInDefaultGroup(groupTitle: string, options: EnumListItem[], groupIndex: number): TemplateResult {
    const title =
      groupTitle !== 'undefined'
        ? html`<gz-title fontSize="11" fontWeight="600" class="list-group--title"
            >${groupTitle.toUpperCase()}</gz-title
          >`
        : '';

    // we display an empty template at group level if the group comes with a fake item
    const content =
      options.length === 1 && options[0].additionalData && options[0].additionalData.type === ItemType.FAKE
        ? html`<div class="list-group--list list-group--no-results">
            ${this.noResultsInDefaultGroup ?? this.noSearchResultsString}
          </div>`
        : html`
            <div class="list-group--list">
              ${options.map((option: EnumListItem) => this.renderFilterOption(option))}
            </div>
          `;

    const classes = classMap({
      'list-group': true,
      'border-bottom': this.listGroup.length > 1 && groupIndex !== this.listGroup.length - 1,
    });

    return html` <div class="${classes}" part="listGroup">${title} ${content}</div> `;
  }

  private renderFilterOption(option: EnumListItem): TemplateResult {
    option = desanitizeObject(option);
    return html`
      <div class="option option-selectable">
        <gz-checkbox
          class="is-filterable full-checkbox"
          value="${option.value.toString()}"
          label="${option.label}"
          ?checked="${option.selected}"
          ?readonly="${option.readonly}"
          ?disabled="${option.disabled}"
          ?isEllipsis="${option.isEllipsis}"
          @change="${e => this.handleCheckboxChange(option, e)}"
        >
        </gz-checkbox>
      </div>
    `;
  }

  private setIsSelectAllChecked(value: boolean) {
    this.isSelectAllChecked = value;
  }

  public handleCheckboxChange(option: EnumListItem, e: CustomEvent) {
    this.updateDisplayedOptionsState(option, e.detail.checked);
    this.updateSelectAllCheckboxesState();
    this.emitFilterOptionToggledEvent();
  }

  protected hasSearchInput(): boolean {
    return this.filterOptions.length > GzEnumListWebComponent.optionsListMaxLength;
  }

  private isOptionEnabled(option: EnumListItem | GzCheckboxWebComponent): boolean {
    return !(option.disabled || option.readonly);
  }

  private getEnabledOptions(): EnumListItem[] {
    return this.filterOptions.filter(option => this.isOptionEnabled(option));
  }

  private getEnabledCheckedOptions(): EnumListItem[] {
    return this.filterOptions.filter(option => this.isOptionEnabled(option) && option.selected);
  }

  private isSelectAllIndeterminate(): boolean {
    const enabledCheckedOptions = this.getEnabledCheckedOptions();
    return enabledCheckedOptions.length !== 0 && enabledCheckedOptions.length < this.getEnabledOptions().length;
  }

  public updateSelectAllCheckboxesState() {
    this.setIsSelectAllChecked(this.getEnabledCheckedOptions().length === this.getEnabledOptions().length);
  }

  protected handleOptionsSearch(e: CustomEvent) {
    const searchString = e.detail.sourceEvent;
    this.setListIsFiltered(!isEmpty(searchString));
    this.filterOptionsBySearchString(searchString);
    this.handleAllValueSearch(searchString);
  }

  protected handleAllValueSearch(search: string) {
    /**
     * On input change && if allCheckboxes option exists:
     * 1. if search string => hide 'ALL' checkbox
     * 2. no search string, i.e. the input was cleared => display 'ALL' checkbox
     */
    if (!this.allCheckboxesSelect) {
      return;
    }

    search
      ? elementClassListAdd(this.allCheckboxesSelect, ElementClass.HIDDEN)
      : elementClassListRemove(this.allCheckboxesSelect, ElementClass.HIDDEN);
  }

  private filterOptionsBySearchString(searchString: string) {
    searchString = searchString.toLowerCase();

    this.searchResult = this.getFilteredResults(searchString);
    this.groupedSearchResult = groupBy(this.searchResult, GzEnumListWebComponent.groupKey);

    // toggle the empty template visibility
    if (isEmpty(this.searchResult)) {
      this.updateEmptyTemplateDisplay(true);
    } else {
      this.updateEmptyTemplateDisplay(false);
    }

    // emit an event that notifies the parent if the list search returned any results. When there are no results, the
    // 'confirm' button on the EnumFilter component should be disabled.
    this.performUpdate();
    this.emitFilterOptionsEvent(this.searchResult);
    this.updateFilteredCheckboxesBySearchResult(this.searchResult);
  }

  public updateEmptyTemplateDisplay(isVisible: boolean) {
    if (!this.emptyTemplate) {
      return;
    }

    if (isVisible) {
      elementClassListAdd(this.emptyTemplate, ElementClass.VISIBLE);
    } else {
      elementClassListRemove(this.emptyTemplate, ElementClass.VISIBLE);
    }
  }

  public getCheckedOptions(): SelectedOption[] {
    return this.filterOptions
      .filter((option: EnumListItem) => option.selected)
      .map(option => {
        return {
          label: option.label,
          value: option.value,
          parent: option.parent,
        };
      });
  }

  public matchCheckboxesToSelectedOptions() {
    this.checkboxesList.forEach((checkbox: GzCheckboxWebComponent) => {
      if (!this.isOptionEnabled(checkbox)) {
        return;
      }
      checkbox.checked =
        this.selectedOptions.filter(selectedOption => this.matchCheckbox(checkbox, selectedOption)).length > 0;
    });
  }

  protected matchCheckbox(checkbox: GzCheckboxWebComponent, selectedOption: SelectedOption) {
    return selectedOption.value === checkbox.value || selectedOption.value === parseInt(checkbox.value, 10);
  }

  public getSelectedValuesFromOptionsList(): SelectedOption[] {
    const selected: SelectedOption[] = [];

    this.filterOptions.forEach((column: EnumListItem) => {
      if (column && column.selected) {
        const { label, value } = column;
        selected.push({ label, value });
      }
    });
    return selected;
  }

  public setSelectedOptions(options: SelectedOption[]) {
    this.selectedOptions = options;
  }

  private toggleAllCheckboxes() {
    this.setIsSelectAllChecked(!this.isSelectAllChecked);

    this.enabledCheckboxes.forEach((checkbox: GzCheckboxWebComponent) => {
      // Update checkboxes
      checkbox.checked = this.isSelectAllChecked;
    });

    // Update selected filterOptions
    this.filterOptions.map((item: EnumListItem) => this.updateDisplayedOptionsState(item, this.isSelectAllChecked));

    this.emitFilterOptionToggledEvent();
  }

  public emitConfirmSelectionEvent() {
    const checkedOptions = this.getCheckedOptions();
    const event = new CustomEvent(EnumListEvents.SELECTION_CHANGE, {
      detail: {
        selectedValues: checkedOptions.map(option => option.value),
        selectedLabels: checkedOptions.map(option => option.label),
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  protected emitFilterOptionToggledEvent() {
    this.dispatchEvent(
      new CustomEvent(EnumListEvents.OPTION_CHANGED, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private emitFilterOptionsEvent(searchResults: EnumListItem[]) {
    const event = new CustomEvent(EnumListEvents.OPTIONS_FILTERED, {
      detail: { resultsFound: !isEmpty(searchResults) },
      composed: true,
    });
    this.dispatchEvent(event);
  }

  private setListIsFiltered(isFiltered: boolean) {
    this.listIsFiltered = isFiltered;
  }

  protected updateDisplayedOptionsState(option: EnumListItem, checked: boolean) {
    this.filterOptions = this.filterOptions.map(item =>
      item.value === option.value && this.isOptionEnabled(option)
        ? {
            ...item,
            selected: checked,
          }
        : item
    );
  }

  protected getFilteredResults(searchString: string) {
    return this.filterOptions.filter((option: EnumListItem) => {
      if (searchString.charAt(0) === ' ') {
        return;
      }

      return option.label.toLowerCase().includes(searchString);
    });
  }
}
